"use client";
import { useTranslations } from "next-intl"

import { ChevronLeft, ChevronRight } from "lucide-react";
import { animate, motion, useMotionValue, useTransform, type MotionValue } from "framer-motion";
import { useRef, useState, useEffect, useCallback, ReactNode } from "react";

import { DURATION, EASE } from "@/lib/motion";
import { Button } from "@/components/ui/button";

export type CardCarouselVariant = "default" | "secondary";

/**
 * The width assumed before the real viewport has been measured — on the server, and for the
 * browser's first render. Any value works as long as BOTH sides use the same one; a desktop
 * width just makes the pre-measurement frame closest to the common case.
 */
const SSR_VIEWPORT_WIDTH = 1280;

/*
  The dots, in pixels, because the pill's width is arithmetic rather than layout — see
  `dotsPillWidth`. Kept beside the classes that draw them: `w-2`/`w-6`/`gap-2` are 8/24/8px, and
  a change to either that is not a change to both puts the pill back to breathing.
*/
const DOT_PX = 8;
const DOT_ACTIVE_PX = 24;
const DOT_GAP_PX = 8;

/**
 * The pill's height, and the padding that follows from it.
 *
 * 32px, the height of every other round control in the app — it was 36, which read as a
 * different family of thing sitting next to the arrows. The padding is then not a taste
 * decision: an 8px dot centred in a 32px pill has 12px of air above and below it, so 12px at
 * the ends is the only value that puts the same gap on all four sides.
 */
const PILL_HEIGHT_PX = 32;
const PILL_PAD_PX = (PILL_HEIGHT_PX - DOT_PX) / 2;

/**
 * One dot, whose width is read from the carousel's position rather than animated on its own.
 *
 * ── Why the width is not a CSS transition ────────────────────────────────────────────────
 *
 * It was, and the dots slid around under a fast scroll. Each dot ran its own 500ms `width`
 * transition, which is fine for a single change: the one shrinking and the one growing move in
 * step, so their combined width never varies. It stops being fine the moment a scroll changes
 * the active card again mid-flight. A retargeted transition restarts from wherever it had got
 * to, with a fresh full duration, so the dots are no longer in step — the combined width
 * wanders, and every dot after the active one wanders with it.
 *
 * `pos` is a single fractional index — 2.4 means "between the third and fourth dot" — and every
 * dot's width is a pure function of it:
 *
 *     width(i) = DOT + (ACTIVE - DOT) × max(0, 1 - |i - pos|)
 *
 * The triangular term sums to exactly 1 across the row for ANY pos, integer or not, so the
 * dots' combined width is invariant by construction rather than by two animations happening to
 * agree. Interrupt it wherever you like: there is one value in flight, and it is retargeted
 * rather than restarted.
 *
 * Opacity stays a CSS class. It does not affect layout, so it cannot move anything — and as a
 * class it can still take a `hover:`, which an inline style from a motion value could not.
 */
function CarouselDot({
  index,
  count,
  pos,
  active,
  label,
  onClick,
}: {
  index: number;
  count: number;
  pos: MotionValue<number>;
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  const width = useTransform(pos, (p) => {
    /*
      Clamped into the row before it is used.

      The triangular term only sums to 1 while `pos` is between the first and last dot. Outside
      that range no dot claims any of it and the row collapses by the full 16px — the exact
      wander this replaced. Nothing should put it out of range today (`activeIndex` is always a
      real card, and the easing here does not overshoot), but "no spring is ever used on this
      value" is a promise about future edits, not a property of the code. Clamping makes the
      invariant hold for any input at all.
    */
    const within = Math.min(Math.max(p, 0), count - 1);
    return DOT_PX + (DOT_ACTIVE_PX - DOT_PX) * Math.max(0, 1 - Math.abs(index - within));
  });
  return (
    <motion.button
      aria-label={label}
      onClick={onClick}
      style={{ width }}
      className={[
        "h-2 shrink-0 rounded-full bg-foreground transition-opacity duration-300",
        active ? "opacity-100" : "opacity-30 hover:opacity-50",
      ].join(" ")}
    />
  );
}

export interface CardCarouselProps {
  variant?: CardCarouselVariant;
  title?: string;
  cards: ReactNode[];
  /**
   * Optional array of URLs, parallel to `cards`. When a URL is provided for
   * a card at index i, that card becomes a link. Indices with no URL (or
   * undefined) remain plain divs.
   */
  cardHrefs?: (string | undefined)[];
  /**
   * Optional click handler, called with the card's index when a card
   * (that does not have an href) is clicked. Ignored for cards that have
   * a corresponding href, since those render as <a> tags and navigate
   * natively instead.
   */
  onCardClick?: (index: number) => void;
  snap?: boolean;
  showDots?: boolean;
  /** Show left/right arrow buttons. Default: false */
  showArrows?: boolean;
  /** Gap between cards in pixels. Default: 32 */
  cardGap?: number;
  cardMaxWidth?: number;
  cardMinHeight?: number;
  /** Show a soft shadow on each card. Default: false */
  cardShadow?: boolean;
  /**
   * Slightly zoom cards on hover. Default: false.
   * Note: incompatible with snap — if both are set, snap is ignored.
   */
  cardHoverZoom?: boolean;
  /**
   * Fill the card with `--accent` on hover. Default: false.
   *
   * For a strip whose cards GO somewhere. A card that navigates and a card that is only a
   * panel look identical at rest here, and the reader finds out which by trying — so the
   * carousel of past work reads as a list until the pointer happens to cross it.
   *
   * `--accent` because that is what a secondary surface in this app turns while it is pointed
   * at, the same as a menu row or a secondary button. Pair it with `cardHrefs` or
   * `onCardClick`: promising a destination the card does not have is worse than the ambiguity
   * it was meant to fix.
   */
  cardHoverFill?: boolean;
}

export function CardCarousel({
  variant = "default",
  title,
  cards,
  cardHrefs,
  onCardClick,
  snap = true,
  showDots = true,
  showArrows = true,
  cardGap = 32,
  cardMaxWidth,
  cardMinHeight = 420,
  cardShadow = false,
  cardHoverZoom = false,
  cardHoverFill = false,
}: CardCarouselProps) {
  const t = useTranslations("layout.CardCarousel")
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  /*
    Where the dots are, as one fractional index. See CarouselDot for why this is a single value
    rather than a transition per dot. Retargeted, never restarted: `animate` picks up from the
    current position and velocity, so changing card mid-flight bends the motion instead of
    beginning a new one.
  */
  const dotPos = useMotionValue(0);
  useEffect(() => {
    const controls = animate(dotPos, activeIndex, { duration: DURATION.base, ease: EASE });
    return () => controls.stop();
  }, [activeIndex, dotPos]);
  const [currentInset, setCurrentInset] = useState(96);
  const [rightInset, setRightInset] = useState(96);
  const [viewportWidth, setViewportWidth] = useState(0);

  // True while a programmatic (click-triggered) smooth scroll is in
  // flight, so the scroll listener below doesn't fight with it.
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Invisible marker that stays inside the page's normal document flow,
  // i.e. inside whatever container (here: max-w-6xl) the caller wraps
  // this component in. We read its position to figure out exactly how
  // far that container's edges sit from the actual viewport edges, then
  // use that to (a) break the scroll track out to true full-bleed via
  // negative margins, compensating independently for each side, and
  // (b) set the left inset so the first card starts flush with where
  // the container's content would have been.
  const insetMarkerRef = useRef<HTMLDivElement>(null);

  const snapActive = snap && !cardHoverZoom;

  useEffect(() => {
    const measure = () => {
      // Measure the CONTAINER, not the marker. The marker is zero-width, so its own
      // `rect.right` equals its `rect.left` — the container's left edge — and taking the
      // right-hand gap from that measured "left edge to the far side of the viewport"
      // instead of the actual right gutter. With a 32px gutter on a 1440px viewport that
      // is 1408 rather than 32, so `naturalCardWidth` came out as
      // 1440 - 32 - 1408 = 0 and every card rendered zero-width. `Math.min(0, …)` is 0,
      // so cardMaxWidth couldn't rescue it either: this collapsed in every container, not
      // just narrow ones.
      //
      // The marker still earns its place — it is what gives us a handle on the container
      // from inside the component, and it is already what the ResizeObserver observes.
      const container = insetMarkerRef.current?.parentElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const leftGap = Math.max(0, Math.round(rect.left));
      const rightGap = Math.max(0, Math.round(viewportW - rect.right));
      setCurrentInset(leftGap);
      setRightInset(rightGap);
      // window.innerWidth excludes the scrollbar gutter, unlike the CSS
      // 100vw unit, which on non-overlay-scrollbar systems (Windows,
      // some Linux desktops) includes it — using CSS 100vw directly for
      // the breakout width below would make the track ~15-17px wider
      // than the real visible viewport and cause horizontal page
      // overflow. Storing the JS-measured width and applying it as a
      // px value sidesteps that entirely.
      setViewportWidth(viewportW);
    };

    measure();
    window.addEventListener("resize", measure);

    // ResizeObserver on the marker's parent: catches container width
    // changes that aren't plain window resizes (sidebar toggles, content
    // reflow elsewhere on the page, etc).
    let ro: ResizeObserver | null = null;
    if (insetMarkerRef.current?.parentElement) {
      ro = new ResizeObserver(measure);
      ro.observe(insetMarkerRef.current.parentElement);
    }

    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, []);

  // Keep activeIndex in sync with free scrolling (swipe/drag/trackpad),
  // not just clicks/arrows. Previously this compared track.scrollLeft
  // against each card's offsetLeft by hand to find the "closest" card —
  // that manual distance math was consistently one card behind during
  // live trackpad scrolling (it only resolved correctly once the scroll
  // came fully to rest, by which point the gesture had moved on).
  // IntersectionObserver sidesteps that entirely: the browser tells us
  // directly which card is actually intersecting the visible (inset-
  // aware) area, continuously, without us re-deriving scroll geometry.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const cardEls = Array.from(track.children) as HTMLElement[];
    if (cardEls.length === 0) return;

    const visibility = new Map<Element, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        // Always record the latest ratios, even while a programmatic
        // scroll is suppressing activeIndex updates below. If we skip
        // this while suppressed, the map goes stale for every card that
        // crossed a threshold during the click-triggered scroll — so the
        // very next free-scroll computation mixes fresh ratios for some
        // cards with leftover pre-click ratios for others, and can easily
        // resolve back to whatever was active before the click.
        entries.forEach((entry) => {
          visibility.set(entry.target, entry.intersectionRatio);
        });

        if (isProgrammaticScroll.current) return;

        let bestIndex = 0;
        let bestRatio = -1;
        cardEls.forEach((el, i) => {
          const ratio = visibility.get(el) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = i;
          }
        });

        setActiveIndex((prev) => (prev === bestIndex ? prev : bestIndex));
      },
      {
        root: track,
        // Mirror the inset on the left so a card only counts as "in
        // view" once it's past the same boundary the snap points use.
        rootMargin: `0px -${currentInset}px 0px 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    cardEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [cards.length, currentInset]);

  const scrollToCard = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(cards.length - 1, index));
    const card = track.children[clamped] as HTMLElement;
    if (!card) return;

    setActiveIndex(clamped);

    // The track has CSS `scroll-padding-left: currentInset` applied (see
    // the style prop below) so the browser's native snap algorithm
    // already accounts for the inset when deciding where "snap-start"
    // lines up. If we manually compute a `left` value for track.scrollTo,
    // we have to replicate that same offset ourselves or the two disagree
    // — our target ends up short by `currentInset`, and snap then pulls
    // the scroll forward onto the *next* card to satisfy the (different)
    // browser-computed snap point. scrollIntoView delegates entirely to
    // the browser's own snap/scroll-padding-aware positioning, so it
    // can't drift out of sync with the CSS like manual math can.
    isProgrammaticScroll.current = true;
    if (programmaticScrollTimeout.current) {
      clearTimeout(programmaticScrollTimeout.current);
    }
    card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    // Smooth scrolls don't have a reliable "finished" callback across
    // browsers, so release the suppression after a generous timeout that
    // comfortably covers the animation.
    programmaticScrollTimeout.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 600);
  }, [cards.length]);

  useEffect(() => {
    return () => {
      if (programmaticScrollTimeout.current) {
        clearTimeout(programmaticScrollTimeout.current);
      }
    };
  }, []);

  const isSecondary = variant === "secondary";
  const sectionBg = isSecondary ? "bg-secondary"  : "bg-background";
  const cardBg    = isSecondary ? "bg-background" : "bg-secondary";
  const pillBg    = isSecondary ? "bg-background" : "bg-secondary";

  // Deliberately NOT `window.innerWidth` as the fallback. Reading the real viewport during
  // render makes the server (which has no window, and so used 1280) and the browser's first
  // render (which used the actual width) disagree about every card's inline width — React
  // reports it as a hydration mismatch and, as it says, does not patch it up. One constant
  // used by both sides agrees by construction; the effect above measures for real a moment
  // later and re-renders with the true width.
  const effectiveViewportWidth = viewportWidth || SSR_VIEWPORT_WIDTH;
  const naturalCardWidth = effectiveViewportWidth - currentInset - rightInset;
  const cardWidth = cardMaxWidth
    ? Math.min(naturalCardWidth, cardMaxWidth)
    : naturalCardWidth;

  /*
    Trailing space, so the LAST card can scroll all the way to the first card's position.

    Without it the track stops when the last card reaches the right edge, which means the
    leftmost fully-visible card can never get past `cards.length - perView`. Since the active
    index is "the leftmost card at full ratio", the final `perView - 1` dots were unreachable —
    permanently, for any number of cards. Every card being able to reach the start position is
    what makes every dot reachable, and it fixes the arrows' `canGoNext` for the same reason.

    The amount is the room to the right of one card sitting at the start position, which falls
    out of the scroll arithmetic independently of how many cards there are:

        maxScroll  = currentInset + contentWidth + trailingPad - viewportWidth
        wanted     = (n - 1) * (cardWidth + gap)        // last card at the start position
        =>  trailingPad = viewportWidth - currentInset - cardWidth

    Only applied when the cards actually overflow. Padding a set that already fits would invent
    a scroll that shouldn't exist and let the whole row be dragged off the left edge.
  */
  const contentWidth =
    cards.length * cardWidth + Math.max(0, cards.length - 1) * cardGap;
  const overflows = contentWidth > effectiveViewportWidth - currentInset - rightInset;
  const trailingPad = overflows
    ? Math.max(rightInset, effectiveViewportWidth - currentInset - cardWidth)
    : rightInset;

  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < cards.length - 1;
  const showControls = (showDots || showArrows) && cards.length > 1;
  const needsOverflowRoom = cardShadow || cardHoverZoom;

  const cardClassName = [
    "flex-shrink-0 px-6 py-6 rounded-2xl",
    snapActive ? "snap-start" : "",
    cardBg,
    cardShadow ? "shadow-[0_0_20px_rgba(0,0,0,0.1)]" : "",
    cardHoverZoom ? "transition-transform duration-300 ease-out hover:scale-[1.02]" : "",
    // `transition-colors` separately from the zoom's `transition-transform`: a card can have
    // either, both, or neither, and `transition-all` on a card that also scales would animate
    // every property the scale touches.
    cardHoverFill ? "transition-colors duration-200 hover:bg-accent" : "",
  ].join(" ");

  /*
    The pill's width, computed rather than measured.

    It used to be `inline-flex`, so the pill was as wide as its dots happened to be AT THAT
    MOMENT — and the dots are mid-animation for half a second after every change of card. Each
    one runs its own 500ms `width` transition, and while the browser keeps a single swap's
    widths summing correctly, a fast scroll retargets a dot that is already in flight: the
    outgoing dot restarts from wherever it had got to, the sum dips below its resting value, and
    the pill breathes in and out around a scroll the reader is in the middle of.

    Sizing it from the DOT COUNT takes the animation out of the layout entirely. The number of
    dots does not change while anything is animating, so the pill cannot either — whatever the
    dots do inside it, and however many times the reader changes their mind on the way.

      px-3.5 ×2 │ one wide dot │ the rest at 8px │ a gap between each
  */
  const dotsPillWidth =
    2 * PILL_PAD_PX + DOT_ACTIVE_PX + Math.max(0, cards.length - 1) * (DOT_PX + DOT_GAP_PX);

  const dotsPill = showDots && (
    <div
      style={{ width: dotsPillWidth }}
      // `justify-center`, so the row is anchored by its middle. A transient dip in the dots'
      // combined width is then split between the two ends instead of leaving a gap at one of
      // them, which is the difference between the dots settling and the dots sliding.
      className={`flex h-8 flex-row items-center justify-center gap-2 px-3 rounded-full ${pillBg}`}
    >
      {cards.map((_, i) => (
        <CarouselDot
          key={i}
          index={i}
          count={cards.length}
          pos={dotPos}
          active={i === activeIndex}
          label={t("goToCard", { number: i + 1 })}
          onClick={() => scrollToCard(i)}
        />
      ))}
    </div>
  );

  /*
    The app's own button, rather than a hand-rolled one.

    These were a bare `<button>` at `w-9 h-9` with their own fill, their own hover and their own
    disabled rules — 36px where every other round control in the app is 32px, and inverting to
    `--foreground` on hover where a secondary control goes to `--accent`. Nothing about a
    carousel arrow needs to differ from a secondary icon button, and `Button` already carries the
    disabled handling these were restating (`pointer-events-none` is why the hover:disabled
    overrides can go: a disabled button cannot be hovered at all).
  */
  const arrowButtons = showArrows && (
    <div className="flex flex-row items-center gap-2">
      <Button
        variant="secondary"
        size="icon"
        type="button"
        aria-label={t("previousCard")}
        onClick={() => scrollToCard(activeIndex - 1)}
        disabled={!canGoPrev}
      >
        <ChevronLeft />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        type="button"
        aria-label={t("nextCard")}
        onClick={() => scrollToCard(activeIndex + 1)}
        disabled={!canGoNext}
      >
        <ChevronRight />
      </Button>
    </div>
  );


  return (
    <div className={`w-full ${sectionBg}`}>

      {/*
        Zero-size marker rendered in normal flow, inside whatever
        container (e.g. max-w-6xl) wraps this component. We measure its
        position relative to the viewport to compute both side gaps,
        which drive the breakout below.
      */}
      <div ref={insetMarkerRef} className="w-0 h-0" aria-hidden="true" />

      {title && (
        <h2
          className="text-4xl font-bold text-foreground mb-12 leading-none"
        >
          {title}
        </h2>
      )}

      <div
        ref={trackRef}
        className={[
          "flex flex-row items-stretch overflow-x-auto",
          "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          snapActive ? "snap-x snap-mandatory" : "",
          needsOverflowRoom ? "py-8 -my-8" : "",
        ].join(" ")}
        style={{
          scrollBehavior: "smooth",
          scrollPaddingLeft: snapActive ? currentInset : undefined,
          // width:100vw can include the scrollbar gutter on some
          // platforms (see viewportWidth measurement above for why); use
          // the JS-measured px value instead so the track's right edge
          // never overshoots the real visible viewport.
          width: viewportWidth || "100vw",
          marginLeft: -currentInset,
          paddingLeft: currentInset,
          paddingRight: trailingPad,
          gap: cardGap,
        }}
      >
        {cards.map((card, i) => {
          const href = cardHrefs?.[i];
          const style = { minHeight: cardMinHeight, width: cardWidth };
          if (href) {
            return (
              <a
                key={i}
                href={href}
                className={`${cardClassName} cursor-pointer`}
                style={style}
              >
                {card}
              </a>
            );
          }
          if (onCardClick) {
            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => onCardClick(i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCardClick(i);
                  }
                }}
                className={`${cardClassName} cursor-pointer`}
                style={style}
              >
                {card}
              </div>
            );
          }
          return (
            <div
              key={i}
              className={cardClassName}
              style={style}
            >
              {card}
            </div>
          );
        })}
      </div>

      {showControls && (
        showArrows
          ? <div className="flex flex-row items-center justify-between mt-3">
              <div>{dotsPill}</div>
              <div>{arrowButtons}</div>
            </div>
          : <div className="flex justify-center mt-8">
              {dotsPill}
            </div>
      )}
    </div>
  );
}
